// src/utils/admissionSeed.ts

export interface SeedOptions {
  patientName?: string;
  patientId?: string;
  appointmentId?: string;
  admittedAt?: Date;

  // Appointment-level data bundles
  complaintsData?: any;
  physicalFindingsData?: any;
  provisionalDiagnosisData?: any;
  definitiveDiagnosisData?: any;
  treatmentData?: any;
  orderedTestsData?: any;

  pastMedicalHistory?: any;
  familyHistory?: any;
  socialHistory?: any;
  medicationHistory?: any;
  mediaData?: any;
  referralData?: any;
}

/**
 * Helper: safely stringify any list of text items (filters empty/nulls)
 */
function list(items?: any[], label?: string): string {
  if (!items || !items.length) return "";
  const text = items
    .map((i) =>
      typeof i === "string"
        ? i
        : i?.name ||
          i?.title ||
          i?.notes ||
          i?.complaint ||
          i?.description ||
          ""
    )
    .filter(Boolean)
    .join(", ");
  return text ? `${label ? label + ": " : ""}${text}\n` : "";
}

/**
 * Helper: flatten history arrays that wrap entries in a key (e.g. "pastMedicalHistories")
 */
function flattenHistory(raw: any, innerKey: string): any[] {
  if (!raw) return [];
  if (!Array.isArray(raw)) return [];

  const out: any[] = [];
  for (const block of raw) {
    if (!block) continue;
    if (Array.isArray(block[innerKey])) {
      out.push(...block[innerKey]);
    } else {
      // already a plain entry (defensive)
      out.push(block);
    }
  }
  return out;
}

/**
 * Build rich preliminary admission documentation content
 * using appointment-level data.
 */
export function buildAdmissionSeedFromAppointment(opts: SeedOptions): string {
  const {
    patientName,
    patientId,
    appointmentId,
    admittedAt,
    complaintsData,
    physicalFindingsData,
    provisionalDiagnosisData,
    definitiveDiagnosisData,
    treatmentData,
    orderedTestsData,
    pastMedicalHistory,
    familyHistory,
    socialHistory,
    medicationHistory,
    mediaData,
    referralData,
  } = opts;

  const lines: string[] = [];

  // --- Header section -------------------------------------------------------
  lines.push("=== Preliminary Admission Documentation ===");

  if (patientName || patientId) {
    lines.push(
      `Patient: ${patientName ?? "Unknown"}${
        patientId ? ` (${patientId})` : ""
      }`
    );
  }

  if (appointmentId) {
    lines.push(`Based on appointment: ${appointmentId}`);
  }

  lines.push(
    `Prepared at: ${
      admittedAt
        ? new Date(admittedAt).toLocaleString()
        : new Date().toLocaleString()
    }\n`
  );

  // --- 1. Main Complaint ----------------------------------------------------
  if (complaintsData?.mainComplaint?.mainComplaint) {
    const mc = complaintsData.mainComplaint;
    lines.push(
      `Main complaint: ${mc.mainComplaint || "See attached main complaint"}`
    );
    if (mc.mainComplaintNote) {
      lines.push(`Main complaint note: ${mc.mainComplaintNote}`);
    }
    if (mc.cond) {
      lines.push(`Main complaint condition/duration: ${mc.cond}`);
    }
    lines.push(""); // blank line
  }

  // --- 2. Other Complaints --------------------------------------------------
  if (complaintsData?.otherComplaints?.length) {
    lines.push("Other complaints:");
    complaintsData.otherComplaints.forEach((oc: any, idx: number) => {
      const base = oc.complaint || `Complaint ${idx + 1}`;
      const note = oc.note;
      const cond = oc.cond;

      let line = `- ${base}`;
      const extras: string[] = [];
      if (cond) extras.push(cond);
      if (note) extras.push(`Note: ${note}`);
      if (extras.length) line += ` (${extras.join("; ")})`;

      lines.push(line);
    });
    lines.push("");
  }

  // --- 3. Past Medical History ---------------------------------------------
  const pmhEntries = flattenHistory(pastMedicalHistory, "pastMedicalHistories");
  if (pmhEntries.length) {
    lines.push("Past medical history:");
    pmhEntries.forEach((item: any, idx: number) => {
      const category = item.category || "Unspecified category";
      const notes = item.notes || "No details provided";
      lines.push(`- ${category}: ${notes}`);
    });
    lines.push("");
  }

  // --- 4. Family History ----------------------------------------------------
  const famEntries = flattenHistory(familyHistory, "familyHistories");
  if (famEntries.length) {
    lines.push("Family history:");
    famEntries.forEach((item: any, idx: number) => {
      const category = item.category || "Unspecified category";
      const member = item.familyMemberAffected || "Family member not specified";
      const notes = item.notes || "No details provided";
      lines.push(`- ${member} – ${category}: ${notes}`);
    });
    lines.push("");
  }

  // --- 5. Social History ----------------------------------------------------
  const socialEntries = flattenHistory(socialHistory, "socialHistories");
  if (socialEntries.length) {
    lines.push("Social history:");
    socialEntries.forEach((item: any, idx: number) => {
      const habits = item.habits || "No habit specified";
      const notes = item.notes || "No details provided";
      lines.push(`- Habits: ${habits}; Notes: ${notes}`);
    });
    lines.push("");
  }

  // --- 6. Medication History ------------------------------------------------
  let medicationEntries: any[] = [];
  if (Array.isArray(medicationHistory?.medications)) {
    medicationEntries = medicationHistory.medications;
  } else if (Array.isArray(medicationHistory)) {
    medicationEntries = medicationHistory;
  }

  if (medicationEntries.length) {
    lines.push("Medication history:");
    medicationEntries.forEach((m: any, idx: number) => {
      const name = m.name || "Unnamed medication";
      const parts: string[] = [];
      if (m.route) parts.push(`route: ${m.route}`);
      if (m.dosage) parts.push(`dose: ${m.dosage}`);
      if (m.frequency) parts.push(`frequency: ${m.frequency}`);
      const mainLine = parts.length
        ? `- ${name} (${parts.join(", ")})`
        : `- ${name}`;
      lines.push(mainLine);
      if (m.notes) {
        lines.push(`  Notes: ${m.notes}`);
      }
    });
    lines.push("");
  }

  // --- 7. Physical Findings -------------------------------------------------
  const physicalArr: any[] = Array.isArray(physicalFindingsData)
    ? physicalFindingsData
    : physicalFindingsData
    ? [physicalFindingsData]
    : [];

  if (physicalArr.length) {
    lines.push("Physical findings:");
    physicalArr.forEach((f: any, idx: number) => {
      const title = f.title || `Finding ${idx + 1}`;
      const detail =
        f.content || f.notes || f.description || "No further details";
      lines.push(`- ${title}: ${detail}`);
    });
    lines.push("");
  }

  // --- 8. Media -------------------------------------------------------------
  const mediaItems: any[] = Array.isArray(mediaData?.data)
    ? mediaData.data
    : Array.isArray(mediaData)
    ? mediaData
    : [];

  if (mediaItems.length) {
    lines.push(
      `Media attached: ${mediaItems.length} item(s) (see media section for details).`
    );
    // Optionally list brief descriptions:
    mediaItems.forEach((m: any, idx: number) => {
      const desc = m.description || m.filename || `Media ${idx + 1}`;
      lines.push(`- ${desc}`);
    });
    lines.push("");
  }

  // --- 9. Provisional & Differential Diagnosis ------------------------------
  const provisionalList = provisionalDiagnosisData?.data?.history || [];
  if (provisionalList.length) {
    lines.push("Differential / provisional diagnoses:");
    provisionalList.forEach((entry: any, idx: number) => {
      if (entry.provisionalDiagnosis) {
        lines.push(`- Provisional diagnosis: ${entry.provisionalDiagnosis}`);
      }
      if (entry.differentialDiagnosis?.length) {
        const diffs = entry.differentialDiagnosis.join(", ");
        lines.push(`  Differential diagnoses: ${diffs}`);
      }
      if (!entry.provisionalDiagnosis && !entry.differentialDiagnosis?.length) {
        lines.push("- No diagnosis recorded for this entry.");
      }
    });
    lines.push("");
  }

  // --- 10. Tests ------------------------------------------------------------
  const testsArr: any[] = Array.isArray(orderedTestsData)
    ? orderedTestsData
    : orderedTestsData
    ? [orderedTestsData]
    : [];

  if (testsArr.length) {
    lines.push("Tests ordered:");
    testsArr.forEach((t: any, idx: number) => {
      const name = t.name || t.code || `Test ${idx + 1}`;
      lines.push(`- ${name}${t.notes ? ` — Notes: ${t.notes}` : ""}`);
    });
    lines.push("");
  }

  // --- 11. Definitive Diagnosis --------------------------------------------
  if (definitiveDiagnosisData?.data) {
    lines.push(`Definitive diagnosis: ${definitiveDiagnosisData.data}`);
    lines.push("");
  }

  // --- 12. Treatment --------------------------------------------------------
  const treatmentArr: any[] = Array.isArray(treatmentData)
    ? treatmentData
    : treatmentData
    ? [treatmentData]
    : [];

  if (treatmentArr.length) {
    lines.push("Treatment plan:");
    treatmentArr.forEach((t: any, idx: number) => {
      const parts: string[] = [];

      if (t.name) parts.push(t.name);
      if (t.route) parts.push(`via ${t.route}`);
      if (t.dosage) parts.push(`(${t.dosage})`);
      if (t.frequency) parts.push(`every ${t.frequency}`);
      if (t.duration) parts.push(`for ${t.duration}`);

      const mainLine = parts.length
        ? `- ${parts.join(" ")}`
        : `- Treatment ${idx + 1}`;

      lines.push(mainLine);

      if (t.notes) {
        lines.push(`  Notes: ${t.notes}`);
      }
    });
    lines.push("");
  }

  // --- 13. Refer Patient ----------------------------------------------------
  const referredArr: any[] = Array.isArray(referralData) ? referralData : [];

  if (referredArr.length) {
    lines.push("Referred patient:");
    referredArr.forEach((doc: any, idx: number) => {
      const name = doc.name || doc.fullName || doc.email || doc._id;
      lines.push(`- ${name}`);
    });
    lines.push("");
  }

  // --- Footer ---------------------------------------------------------------
  lines.push("[End of preliminary admission notes]");
  return lines.join("\n");
}
